/**
 * The widget's built-in visitor copy: what renders when a partner leaves a
 * `copy.*` (or `safety.emergency_message_md`) field empty.
 *
 * One module, no runtime dependencies beyond other dependency-free files, so
 * the partner portal imports it directly and shows each default as the empty
 * field's placeholder. A portal placeholder that is typed by hand drifts the
 * first time the widget's wording changes; this cannot.
 */

export { DEFAULT_AUDIO_NOTICE, defaultNoticeAtCollection } from './consentText';

/** `copy.headline`. */
export const DEFAULT_HEADLINE = 'Snap it. Fix it.';

/** The line under the headline (`copy.subheadline` overrides it). */
export const DEFAULT_SUBHEADLINE = "Get a quick AI diagnosis for what's broken.";

/** `copy.cta_app` in the server's defaults (the app handoff button). */
export const DEFAULT_APP_CTA_LABEL = 'Continue in the app';

/** `copy.cta_route` for the modes that have a route button. */
export function defaultRouteCtaLabel(kind: 'form' | 'call', partner: string | null | undefined): string {
  if (kind === 'call') return partner ? `Call ${partner}` : 'Call now';
  return partner ? `Send to ${partner}` : 'Send this in';
}

/** Shown when the trade is out of scope and the partner wrote no copy. */
export const DEFAULT_OUT_OF_SCOPE_MESSAGE = "This isn't something we handle here.";

/** `copy.out_of_scope_message`, naming the partner when there is one: a bare
 *  "we" on a partner's page read as FinderlyFix turning the job down. */
export function defaultOutOfScopeMessage(partner: string | null | undefined): string {
  return partner ? `${partner} doesn't handle this type of job.` : DEFAULT_OUT_OF_SCOPE_MESSAGE;
}

/** `copy.sent_confirmation_matched` / `_unmatched`: the body under "Sent".
 *  Says what happened (it was sent) and never promises a call: routing may
 *  not match the job to a pro (production e2e, 2026-09-27). */
export function defaultSentConfirmation(partner: string | null | undefined, matched: boolean): string {
  const sent = partner ? `We've sent this to ${partner}.` : "We've sent your request.";
  return `${sent} ${matched ? "They'll be in touch about next steps." : "They'll review it and reach out if they can help."}`;
}

// Gas: leave, then call from outside — 911 or the gas company's emergency
// line, as utilities advise. Fire, smoke and CO: 911. Flooding is not a 911
// tier here (burst_pipe escalates to "contractor"), so it is not named.
/** `safety.emergency_message_md`. */
export const DEFAULT_EMERGENCY_MESSAGE =
  "If you smell gas, leave the building, then call 911 or your gas company's emergency line from outside. If you see fire or smoke, leave and call 911.";

/** `copy.live_notice`: shown before a live conversation asks for the microphone.
 *
 * Only what the law asks for before the microphone opens (owner decision,
 * 2026-09-26): that the visitor is talking to an AI, not a person (Maine
 * 10 MRSA §1500-DD, CA B&P §17941), and what it listens to, shown before the
 * session starts (the CIPA §631 prior-consent line). The old "nothing is kept
 * after you get your result" was dropped rather than reworded: the result is
 * kept for up to an hour and goes to the partner with a lead.
 */
export function defaultLiveNotice(camera: boolean): string {
  return (
    "You'll be talking with an AI assistant, not a person. It listens to what you say" +
    (camera ? ', and sees your camera if you turn it on,' : '') +
    " to work out what's wrong. Your voice and video aren't recorded or sold. Privacy: finderlyfix.com/PrivacyPolicy"
  );
}

/**
 * `copy.live_notice_short` (spec 014): the one line always visible above the
 * Talk live / Chat buttons, before anything opens. It carries the whole legal
 * floor on its own (an AI, not a person; it listens), so the full notice can
 * sit behind "More". `chat` = Chat is offered too.
 */
export function defaultLiveNoticeShort(input: { voice: boolean; chat: boolean; camera?: boolean }): string {
  const sees = input.camera ? ', and sees your camera if you turn it on' : '';
  if (input.voice && input.chat) {
    return `You'll be talking or chatting with an AI assistant, not a person. Talk live listens to what you say${sees}.`;
  }
  if (input.chat) return defaultChatNotice();
  return `You'll be talking with an AI assistant, not a person. It listens to what you say${sees}.`;
}

/** `copy.chat_notice` (spec 014): the AI disclosure for chat. No microphone. */
export function defaultChatNotice(): string {
  return "You'll be chatting with an AI assistant, not a person.";
}

/** The "More" text under the short line: the full notice(s) for what is offered. */
export function defaultChatDetails(): string {
  return "It reads what you type to work out what's wrong. Your messages aren't sold. Privacy: finderlyfix.com/PrivacyPolicy";
}
